import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from 'shared/hooks/useAuth';
import { formatEnumLabel } from '../../../constants/enums';
import ChangeManagementDialog, { emptyChangeManagementRow } from '../../../shared/ChangeManagementDialog';
import { apiErrorMessage } from '../../../utils/responseHelpers';
import { persistWizardNestedCollection } from '../../../utils/persistWizardNestedCollection';
import { isChangeLocked } from '../../change-management/changeApprovalState';

const APPROVAL_STATUS_COLORS = {
  full: { bg: '#E8F5E9', color: '#1B5E20' },
  partial: { bg: '#FFF8E1', color: '#F57F17' },
  declined: { bg: '#FFEBEE', color: '#B71C1C' },
  none: { bg: '#ECEFF1', color: '#455A64' },
};

function slotRole(slot) {
  return slot === 1 ? '1st Approval' : '2nd Approval';
}

function findSlotApproval(approvals, slot) {
  const role = slotRole(slot);
  return (approvals ?? []).find((a) => a?.approverRole === role) || null;
}

/** Pull the free-text note from stored approvalComments (`… | Comment: …`). */
function extractApprovalComment(approvalComments) {
  if (!approvalComments?.trim()) return '';
  const match = String(approvalComments).match(/\|\s*Comment:\s*([\s\S]+)$/i);
  if (match) return match[1].trim();
  return String(approvalComments).trim();
}

/** Declined slots with comments — shown under Approval status only when declined. */
function getDeclinedComments(approvals) {
  return (approvals ?? [])
    .filter((a) => a?.approvalStatus === 'REJECTED')
    .map((a) => ({
      role: a.approverRole || 'Approver',
      comment: extractApprovalComment(a.approvalComments),
      approverName: a.approverName || '',
    }))
    .filter((row) => row.comment);
}

/** Summarize 1st/2nd approval state for the change-management table. */
function getApprovalSummary(approvals) {
  const first = findSlotApproval(approvals, 1);
  const second = findSlotApproval(approvals, 2);
  const firstApproved = first?.approvalStatus === 'APPROVED';
  const secondApproved = second?.approvalStatus === 'APPROVED';
  const anyDeclined = [first, second].some((a) => a?.approvalStatus === 'REJECTED');
  const declinedComments = anyDeclined ? getDeclinedComments(approvals) : [];

  if (firstApproved && secondApproved) {
    return { key: 'full', label: 'Fully approved', declinedComments: [] };
  }
  if (anyDeclined) {
    return { key: 'declined', label: 'Declined', declinedComments };
  }
  if (firstApproved || secondApproved) {
    const pending = firstApproved ? '2nd' : '1st';
    return { key: 'partial', label: `Awaiting ${pending} approval`, declinedComments: [] };
  }
  return { key: 'none', label: 'Not approved', declinedComments: [] };
}

/**
 * Change Management wizard step: table + add/edit dialog.
 */
export default function WizardStepChangeManagement({
  values,
  onSetField,
  onBeforeView,
  softwareName = '',
  softwareId = null,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const navigate = useNavigate();
  const { user } = useAuth();
  const requestedByDefault = user?.data?.email?.trim() || user?.data?.name?.trim() || '';

  const entries = values?.entries ?? [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const approvalChipSx = useMemo(
    () => (key) => {
      const palette = APPROVAL_STATUS_COLORS[key] || APPROVAL_STATUS_COLORS.none;
      return {
        bgcolor: isDark ? alpha(palette.color, 0.28) : palette.bg,
        color: isDark ? '#fff' : palette.color,
        fontWeight: 700,
      };
    },
    [isDark]
  );

  const openCreate = () => {
    if (!softwareId) {
      toast.error('Save system identification first so changes can be linked to this software.');
      return;
    }
    setDialogMode('create');
    setEditing(emptyChangeManagementRow({ requestedBy: requestedByDefault }));
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    if (isChangeLocked(row)) {
      toast.error('This change is fully approved and cannot be edited.');
      return;
    }
    setDialogMode('edit');
    setEditing({ ...row });
    setDialogOpen(true);
  };

  const openView = (row) => {
    onBeforeView?.();
    navigate('/system-register/change-managements/view', {
      state: {
        change: row,
        softwareName,
        softwareId: softwareId ?? null,
        returnTo: `${window.location.pathname}${window.location.search}`,
      },
    });
  };

  const persistEntries = async (nextEntries) => {
    const persisted = await persistWizardNestedCollection(
      softwareId,
      'changeManagements',
      nextEntries
    );
    onSetField?.('entries', persisted);
    return persisted;
  };

  const removeRow = async (key) => {
    if (!softwareId) {
      onSetField?.('entries', entries.filter((e) => e.key !== key));
      return;
    }
    setSaving(true);
    try {
      await persistEntries(entries.filter((e) => e.key !== key));
      toast.success('Change removed.');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to remove change.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (row) => {
    const next =
      dialogMode === 'edit'
        ? entries.map((e) => (e.key === row.key ? row : e))
        : [...entries, row];

    setSaving(true);
    try {
      await persistEntries(next);
      toast.success(
        dialogMode === 'edit' ? 'Change updated.' : 'Change saved and linked to this software.'
      );
      setDialogOpen(false);
      setEditing(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save change.'));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1.5} gap={1} flexWrap="wrap">
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          Add change
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: `1px solid ${alpha(primary, isDark ? 0.35 : 0.14)}`,
          borderRadius: 2,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Requested by</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Approval status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
                    No change records yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((row) => {
                const approval = getApprovalSummary(row.approvals);
                const locked = isChangeLocked(row);
                return (
                  <TableRow key={row.key}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.title || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatEnumLabel(row.changeType) || '—'}</TableCell>
                    <TableCell>{formatEnumLabel(row.priority) || '—'}</TableCell>
                    <TableCell>
                      {row.implementationStatus ? (
                        <Chip size="small" label={formatEnumLabel(row.implementationStatus)} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{row.requestedBy || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280, verticalAlign: 'top' }}>
                      <Chip size="small" label={approval.label} sx={approvalChipSx(approval.key)} />
                      {approval.declinedComments?.length > 0 ? (
                        <Box mt={0.75} display="flex" flexDirection="column" gap={0.5}>
                          {approval.declinedComments.map((item) => (
                            <Box key={`${row.key}-${item.role}`}>
                              <Typography
                                variant="caption"
                                display="block"
                                color="error.main"
                                fontWeight={700}
                              >
                                {item.role}
                                {item.approverName ? ` · ${item.approverName}` : ''}
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.35 }}
                              >
                                {item.comment}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => openView(row)} disabled={saving}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={locked ? 'Fully approved changes cannot be edited' : 'Edit'}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(row)}
                            disabled={saving || locked}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => removeRow(row.key)} disabled={saving}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ChangeManagementDialog
        open={dialogOpen}
        mode={dialogMode}
        value={editing}
        saving={saving}
        onClose={() => {
          if (saving) return;
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />
    </Box>
  );
}
